<script lang="ts">
  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, SAILFISH_PRODUCT_SCHEMA, SAILFISH_FAQ_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/products/sailfish"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    SAILFISH_PRODUCT_SCHEMA,
    SAILFISH_FAQ_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Products", url: "https://0xfire.com/products/sailfish" },
      { name: "Sailfish", url: "https://0xfire.com/products/sailfish" },
    ]),
  ));
  import { route } from "@mateothegreat/svelte5-router";
  import {
    CheckIcon,
    ArrowRight,
    GithubIcon,
    BookOpenTextIcon,
    MessageCircleIcon,
    Radio,
    Database,
    Code,
    Zap,
  } from "lucide-svelte";
  import { PRODUCTS_SERVICES } from "../../../data/offers";
  import ProductCard from "../../elements/ProductCard.svelte";

  const products = [
    { name: "Solana", status: "live", demo: "/tools/solana", description: "Trades, pool inits, token events, graduations", color: "violet" },
    { name: "Polymarket", status: "live", demo: "/tools/polymarket", description: "Institutional orderbook streaming", color: "sky" },
    { name: "Ethereum", status: "live", demo: null, description: "DEX trades and pool events", color: "indigo" },
    { name: "Binance Chain", status: "live", demo: null, description: "BSC DEX trades and pool events", color: "yellow" },
    { name: "Hyperliquid", status: "live", demo: null, description: "Perpetuals orderbook and trades", color: "cyan" },
    { name: "Kalshi", status: "live", demo: null, description: "Event contracts orderbook streaming", color: "emerald" },
  ];

  const BUY_URL = "https://cloud.solanavibestation.com/index.php?/cart/sailfish-api---streamer/";

  const colorMap: Record<string, { text: string; border: string; bg: string; hoverBorder: string }> = {
    violet: { text: "text-violet-500", border: "border-violet-500/30", bg: "bg-violet-500", hoverBorder: "hover:border-violet-900/40" },
    sky: { text: "text-sky-500", border: "border-sky-500/30", bg: "bg-sky-500", hoverBorder: "hover:border-sky-900/40" },
    indigo: { text: "text-indigo-500", border: "border-indigo-500/30", bg: "bg-indigo-500", hoverBorder: "hover:border-indigo-900/40" },
    yellow: { text: "text-yellow-500", border: "border-yellow-500/30", bg: "bg-yellow-500", hoverBorder: "hover:border-yellow-900/40" },
    cyan: { text: "text-cyan-500", border: "border-cyan-500/30", bg: "bg-cyan-500", hoverBorder: "hover:border-cyan-900/40" },
    emerald: { text: "text-emerald-500", border: "border-emerald-500/30", bg: "bg-emerald-500", hoverBorder: "hover:border-emerald-900/40" },
  };
</script>

<div class="min-h-screen bg-black text-gray-300 selection:bg-sky-900 selection:text-white">
  <!-- Hero -->
  <div class="relative overflow-hidden">
    <!-- Sailfish backdrop -->
    <img
      src="/sailfish_web.png"
      alt=""
      role="presentation"
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[800px] opacity-[0.07] pointer-events-none select-none"
    />
    <div class="relative text-center py-20 px-4">
      <img src="/sailfish_web.webp" alt="Sailfish" class="h-24 md:h-32 w-auto mx-auto mb-6 opacity-90" width="800" height="388" loading="lazy" />
      <div class="badge badge-outline rounded-none uppercase tracking-widest font-mono text-xs text-sky-500 border-sky-500/50 p-3 mb-6">
        Sailfish
      </div>
      <h1 class="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
        Real-Time Trading <span class="text-sky-500">Infrastructure</span>
      </h1>
      <p class="text-xl text-gray-500 max-w-2xl mx-auto mb-8">
        Stream live trades, orderbooks, and token events across multiple chains. Build, test & deploy trading bots with production-grade data.
      </p>
      <div class="flex flex-wrap gap-2 justify-center">
        <a use:route href="/products/sailfish/docs" class="btn btn-sm bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 rounded-sm transition-colors">
          <BookOpenTextIcon class="size-4" /> Docs
        </a>
        <a href="https://github.com/0xfire-org/sailfish-sdk" target="_blank" class="btn btn-sm bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 rounded-sm transition-colors">
          <GithubIcon class="size-4" /> SDK
        </a>
        <a href="https://discord.gg/cbtejQCpSh" target="_blank" class="btn btn-sm bg-gray-900 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 rounded-sm transition-colors">
          <MessageCircleIcon class="size-4" /> Discord
        </a>
      </div>
    </div>
  </div>

  <!-- Products -->
  <div class="border-t border-gray-800">
    <div class="max-w-5xl mx-auto py-20 px-4">
      <h2 class="text-[11px] font-mono text-center text-gray-600 mb-12 uppercase tracking-[0.4em]">
        Products
      </h2>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {#each products as product}
          {@const c = colorMap[product.color]}
          {@const isLive = product.status === "live"}
          <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-6 pt-0 overflow-hidden flex flex-col">
            <div class="h-1 {c.bg} -mx-6 mb-5"></div>
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-lg font-bold text-white">{product.name}</h3>
              {#if isLive}
                <span class="text-[9px] font-mono uppercase tracking-widest {c.text} border {c.border} rounded-none px-2 py-0.5">
                  Live
                </span>
              {:else}
                <span class="text-[9px] font-mono uppercase tracking-widest text-gray-600 border border-gray-700 rounded-none px-2 py-0.5">
                  Coming Soon
                </span>
              {/if}
            </div>
            <p class="text-sm text-gray-500 mb-4">{product.description}</p>
            <div class="flex items-baseline gap-1 mb-5 mt-auto">
              <span class="text-2xl font-black text-white">$75</span>
              <span class="text-gray-500 text-sm">/month</span>
            </div>
            <div class="flex gap-2">
              <a
                href={product.name === "Solana" ? BUY_URL : "https://calendly.com/0xconstantin/30min"}
                target="_blank"
                class="btn btn-sm {c.bg} hover:opacity-90 text-white border-none rounded-sm uppercase tracking-widest font-bold text-xs transition-all flex-1"
              >
                Subscribe
              </a>
              {#if product.demo}
                <a
                  use:route
                  href={product.demo}
                  class="btn btn-sm bg-transparent border border-gray-700 hover:border-gray-600 text-gray-400 hover:text-white rounded-sm uppercase tracking-widest font-bold text-xs transition-colors flex-1"
                >
                  <span class="relative flex h-2 w-2 mr-1.5">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full {c.bg} opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 {c.bg}"></span>
                  </span>
                  Demo
                </a>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>

  <!-- Features -->
  <div class="border-t border-gray-800">
    <div class="max-w-5xl mx-auto py-20 px-4">
      <h2 class="text-[11px] font-mono text-center text-gray-600 mb-12 uppercase tracking-[0.4em]">
        Features
      </h2>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        <div class="flex items-start gap-4">
          <div class="w-10 h-10 rounded-sm bg-sky-600/10 border border-sky-600/20 flex items-center justify-center shrink-0">
            <Radio class="size-5 text-sky-500" />
          </div>
          <div>
            <h3 class="text-sm font-bold text-white mb-1">Real-Time Streaming</h3>
            <p class="text-sm text-gray-500">WebSocket streams for trades, pool initializations, token mints, and graduations with sub-second latency.</p>
          </div>
        </div>

        <div class="flex items-start gap-4">
          <div class="w-10 h-10 rounded-sm bg-sky-600/10 border border-sky-600/20 flex items-center justify-center shrink-0">
            <Database class="size-5 text-sky-500" />
          </div>
          <div>
            <h3 class="text-sm font-bold text-white mb-1">Historic API</h3>
            <p class="text-sm text-gray-500">Query historical trades, pool metadata, and token information by block range, address, or pool type.</p>
          </div>
        </div>

        <div class="flex items-start gap-4">
          <div class="w-10 h-10 rounded-sm bg-sky-600/10 border border-sky-600/20 flex items-center justify-center shrink-0">
            <Code class="size-5 text-sky-500" />
          </div>
          <div>
            <h3 class="text-sm font-bold text-white mb-1">Open Source SDK</h3>
            <p class="text-sm text-gray-500">TypeScript SDK with full type safety. npm install from GitHub. Built-in caching and pool info enrichment.</p>
          </div>
        </div>

        <div class="flex items-start gap-4">
          <div class="w-10 h-10 rounded-sm bg-sky-600/10 border border-sky-600/20 flex items-center justify-center shrink-0">
            <Zap class="size-5 text-sky-500" />
          </div>
          <div>
            <h3 class="text-sm font-bold text-white mb-1">Low Latency</h3>
            <p class="text-sm text-gray-500">20-50ms price stream latency. 99.9% uptime. 2.5TB daily bandwidth. Production infrastructure.</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- What is Sailfish — Entity Definition (SEO) -->
  <div class="border-t border-gray-800">
    <div class="max-w-3xl mx-auto py-20 px-4">
      <h2 class="text-2xl font-bold text-white mb-6 tracking-tight">What is Sailfish?</h2>
      <div class="space-y-4 text-sm text-gray-400 leading-relaxed">
        <p>
          Sailfish is a real-time trading data API built by <a use:route href="/" class="text-sky-500 hover:underline">0xfire</a>. It provides WebSocket streaming and REST API access to live and historic market data across Solana, Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi.
        </p>
        <p>
          Unlike general blockchain RPC providers, Sailfish focuses exclusively on parsed, structured trading data — trades, pool initializations, token mints, graduations, and orderbook updates — rather than raw transaction data. This means developers can subscribe to trade events without parsing transaction logs or managing RPC connections.
        </p>
        <p>
          On Solana, Sailfish supports Raydium (AMM, CPMM, CLMM, LaunchLab), PumpFun, PumpSwap, and Meteora (DYN, DYN2, DLMM). Sailfish also streams live data from Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi.
        </p>
        <p>
          The <a href="https://github.com/0xfire-org/sailfish-sdk" target="_blank" class="text-sky-500 hover:underline">open-source TypeScript SDK</a> provides type-safe callbacks for all event types, built-in pool info caching, and filtering by token address, pool address, or DEX type. Pricing starts at $75/month per chain.
        </p>
      </div>
    </div>
  </div>

  <!-- Performance / Benchmarks -->
  <div class="border-t border-gray-800">
    <div class="max-w-3xl mx-auto py-20 px-4">
      <h2 class="text-2xl font-bold text-white mb-8 tracking-tight">Performance</h2>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div>
          <p class="text-2xl font-black text-white">20-50<span class="text-sky-500">ms</span></p>
          <p class="text-xs text-gray-500 mt-1">Stream latency</p>
        </div>
        <div>
          <p class="text-2xl font-black text-white">99.9<span class="text-sky-500">%</span></p>
          <p class="text-xs text-gray-500 mt-1">Uptime</p>
        </div>
        <div>
          <p class="text-2xl font-black text-white">2.5<span class="text-sky-500">TB</span></p>
          <p class="text-xs text-gray-500 mt-1">Daily bandwidth</p>
        </div>
        <div>
          <p class="text-2xl font-black text-white">10<span class="text-sky-500">+</span></p>
          <p class="text-xs text-gray-500 mt-1">Supported DEX protocols</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Comparison Table (citation-bait for LLMs) -->
  <div class="border-t border-gray-800">
    <div class="max-w-4xl mx-auto py-20 px-4">
      <h2 class="text-2xl font-bold text-white mb-8 tracking-tight">How Sailfish Compares</h2>
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-gray-800">
              <th class="py-3 pr-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Feature</th>
              <th class="py-3 px-4 text-sky-500 font-mono text-xs uppercase tracking-wider">Sailfish</th>
              <th class="py-3 px-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Birdeye</th>
              <th class="py-3 px-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Helius</th>
              <th class="py-3 px-4 text-gray-500 font-mono text-xs uppercase tracking-wider">Shyft</th>
            </tr>
          </thead>
          <tbody class="text-gray-400">
            <tr class="border-b border-gray-800/50">
              <td class="py-3 pr-4 text-gray-300">Real-time trade streaming</td>
              <td class="py-3 px-4 text-sky-400">WebSocket</td>
              <td class="py-3 px-4">REST polling</td>
              <td class="py-3 px-4">Webhooks</td>
              <td class="py-3 px-4">Webhooks</td>
            </tr>
            <tr class="border-b border-gray-800/50">
              <td class="py-3 pr-4 text-gray-300">Stream latency</td>
              <td class="py-3 px-4 text-sky-400">20-50ms</td>
              <td class="py-3 px-4">1-5s</td>
              <td class="py-3 px-4">1-5s</td>
              <td class="py-3 px-4">1-5s</td>
            </tr>
            <tr class="border-b border-gray-800/50">
              <td class="py-3 pr-4 text-gray-300">Historic trade queries</td>
              <td class="py-3 px-4 text-sky-400">REST API</td>
              <td class="py-3 px-4">Yes</td>
              <td class="py-3 px-4">Limited</td>
              <td class="py-3 px-4">Yes</td>
            </tr>
            <tr class="border-b border-gray-800/50">
              <td class="py-3 pr-4 text-gray-300">Open-source SDK</td>
              <td class="py-3 px-4 text-sky-400">TypeScript</td>
              <td class="py-3 px-4">No</td>
              <td class="py-3 px-4">No</td>
              <td class="py-3 px-4">No</td>
            </tr>
            <tr class="border-b border-gray-800/50">
              <td class="py-3 pr-4 text-gray-300">Parsed trade events</td>
              <td class="py-3 px-4 text-sky-400">Yes (Trade + TradeRaw)</td>
              <td class="py-3 px-4">Yes</td>
              <td class="py-3 px-4">Raw tx parsing</td>
              <td class="py-3 px-4">Yes</td>
            </tr>
            <tr class="border-b border-gray-800/50">
              <td class="py-3 pr-4 text-gray-300">Pool init events</td>
              <td class="py-3 px-4 text-sky-400">Real-time + Historic</td>
              <td class="py-3 px-4">No</td>
              <td class="py-3 px-4">Webhooks</td>
              <td class="py-3 px-4">No</td>
            </tr>
            <tr>
              <td class="py-3 pr-4 text-gray-300">Starting price</td>
              <td class="py-3 px-4 text-sky-400">$75/mo</td>
              <td class="py-3 px-4">$49/mo</td>
              <td class="py-3 px-4">$49/mo</td>
              <td class="py-3 px-4">$49/mo</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- FAQ Section -->
  <div class="border-t border-gray-800">
    <div class="max-w-3xl mx-auto py-20 px-4">
      <h2 class="text-2xl font-bold text-white mb-8 tracking-tight">Frequently Asked Questions</h2>
      <div class="space-y-6">
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What protocols does Sailfish support on Solana?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Sailfish supports Raydium (AMM, CPMM, CLMM, LaunchLab), PumpFun, PumpSwap, and Meteora (DYN, DYN2, DLMM) with both real-time streaming and historic API access for trades and pool initializations.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What is the difference between Trade and TradeRaw?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Trade is a human-readable format with decimal amounts and buy/sell classification based on PoolInfo. TradeRaw provides raw token amounts without PoolInfo dependency, giving you full control over interpretation.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">How do I get started with the Sailfish SDK?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Install via <code class="text-xs text-gray-400 bg-gray-900 px-1 py-0.5 rounded">npm install github:0xfire-org/sailfish-sdk</code>, then use the typed callbacks (onTrade, onPoolInit, onTokenMint, etc.) to handle events. See the <a href="https://github.com/0xfire-org/sailfish-examples" target="_blank" class="text-sky-500 hover:underline">examples repo</a> for working code.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What is Sailfish's latency compared to RPC nodes?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Sailfish streams parsed trades at 20-50ms latency. Unlike raw RPC subscriptions, you receive structured trade data without needing to parse transaction logs, instruction data, or inner instructions yourself.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Can I use Sailfish for backtesting?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Yes. The Historic API provides trade and pool data queryable by block range, token address, or pool address. You can build candlestick data, replay trades, and test strategies against real market data.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What chains does Sailfish support?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Yes. Sailfish supports Solana, Ethereum, Binance Chain, Hyperliquid, Polymarket, and Kalshi. All chains are live with real-time streaming and historic API access.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Related Links -->
  <div class="border-t border-gray-800">
    <div class="max-w-3xl mx-auto py-12 px-4">
      <p class="text-xs font-mono text-gray-600 uppercase tracking-widest mb-4">Related</p>
      <div class="flex flex-wrap gap-3">
        <a use:route href="/tools/solana" class="text-sm text-gray-500 hover:text-white transition-colors">Solana Live Dashboard</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/products/sailfish/docs" class="text-sm text-gray-500 hover:text-white transition-colors">API Docs</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/tools/solana/historic" class="text-sm text-gray-500 hover:text-white transition-colors">Historic Trade Data</a>
        <span class="text-gray-700">&middot;</span>
        <a use:route href="/services" class="text-sm text-gray-500 hover:text-white transition-colors">Bot Development Services</a>
        <span class="text-gray-700">&middot;</span>
        <a href="https://github.com/0xfire-org/sailfish-sdk" target="_blank" class="text-sm text-gray-500 hover:text-white transition-colors">SDK on GitHub</a>
      </div>
    </div>
  </div>

  <!-- Bottom CTA -->
  <div class="border-t border-gray-800 py-16 px-4 text-center">
    <p class="text-gray-500 mb-4">Questions? Need a custom integration?</p>
    <a
      href="https://calendly.com/0xconstantin/30min"
      target="_blank"
      class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold px-10 transition-colors"
    >
      Book a Call
    </a>
  </div>
</div>
